import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import IngredientHeader from 'components/desktop/Ingredient/IngredientHeader';
import * as IngredientState from 'components/desktop/Ingredient/IngredientState';
import TabCo2Footprints from 'components/desktop/Ingredient/Tabs/TabCo2Footprints';
import TabErp from 'components/desktop/Ingredient/Tabs/TabErp';
import TabGeneral from 'components/desktop/Ingredient/Tabs/TabGeneral';
import TabMetaData from 'components/desktop/Ingredient/Tabs/TabMetaData';
import TabNutrients from 'components/desktop/Ingredient/Tabs/TabNutrients';
import { IngredientDetailData } from 'components/desktop/Ingredient/useIngredientData';
import useIngredientData from 'components/desktop/Ingredient/useIngredientData';
import useIngredientState from 'components/desktop/Ingredient/useIngredientState';
import { IngredientDetailHandlers } from 'components/desktop/Ingredient/useIngredientState';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Tabs, { Tab } from 'components/desktop/_general/Tabs/Tabs';
import { Optional } from 'functions/promiseExtensions';
import useCultureCode from 'hooks/redux/useCultureCode';
import useCurrency from 'hooks/redux/useCurrency';
import useAirBlocker from 'hooks/useAirBlocker';
import { useIsHandlerLoaded } from 'hooks/useIsLoading';
import { useScrollToTop } from 'hooks/useScrollToTop';
import * as Ingredient from 'types/Ingredient/Detail/Ingredient';

interface IProps {
	id?: string;
	initialState: Ingredient.Type;
	isLoading: boolean;
}

const IngredientComponent = (props: IProps) => {
	useScrollToTop();
	const { t } = useTranslation();
	const cultureCode: string = useCultureCode();
	const currencyCode: string = useCurrency();
	const { state, handlers } = useIngredientState(
		Ingredient.mapToState(props.initialState, cultureCode)
	);
	const data = useIngredientData();

	const putIngredient = async (): Promise<void> => {
		const newState = await IngredientState.save(state, cultureCode, currencyCode);

		handlers.load(newState);
		await updateCache(newState);
	};

	const postIngredient = async (): Promise<void> => {
		const newState = await IngredientState.save(state, cultureCode, currencyCode);
		if (newState.id) {
			await updateCache(state);
			navigate(`/ingredient/detail/${newState.id}`);
		}
	};

	const [isLoaded, handleSave] = useIsHandlerLoaded(
		Optional.WrapToJust(state, state.id ? putIngredient : postIngredient)
	);

	const { updateCache } = useAirBlocker(state, handleSave);
	const navigate = useNavigate();

	const tabs: Tab[] = [
		{
			name: t('_general:GENERAL'),
			dataComponentType: 'general information',
			content: <TabGeneral state={state} handlers={handlers} data={data} />,
			ai: false,
		},
		{
			name: t('_general:NUTRIENTS'),
			dataComponentType: 'nutrient-information-tab',
			content: <TabNutrients state={state} handlers={handlers} data={data} />,
			ai: false,
		},
		{
			name: t('recipe:METADATA'),
			dataComponentType: 'metadata-information-tab',
			content: <TabMetaData state={state} handlers={handlers} data={data} />,
			ai: false,
		},
		{
			name: t('_general:CO2_FOOTPRINTS'),
			dataComponentType: 'co2-footprints-tab',
			content: <TabCo2Footprints state={state} handlers={handlers} data={data} />,
			ai: false,
		},
		{
			name: t('ingredient:PRODUCTS'),
			dataComponentType: 'erp-information-tab',
			content: <TabErp state={state} handlers={handlers} data={data} />,
			ai: true,
		},
	];

	return (
		<>
			<LoadingAnimation isLoading={!isLoaded || props.isLoading} />
			<IngredientHeader
				state={state}
				data={data}
				handleSave={handleSave}
				isLoading={!isLoaded || props.isLoading}
				readOnly={false}
			/>
			<br />
			<Tabs
				localStorageKey={'activeIngredientTab'}
				tabs={tabs.filter((t: Tab) => {
					if (t.display === undefined || t.display === true) {
						return t;
					}
				})}
			/>
		</>
	);
};

export default IngredientComponent;

export interface IngredientTabProps {
	state: IngredientState.Type;
	handlers: IngredientDetailHandlers;
	data: IngredientDetailData;
}
